import { useSyncExternalStore } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDClient, LDContext } from 'launchdarkly-js-client-sdk';

/**
 * Which dispensary LaunchDarkly is currently serving flags for.
 *
 * Needed because neither of the obvious signals answers that question:
 *
 * - `client.getContext()` is only read during render, and the React SDK re-renders consumers from
 *   the `change` event, which the client emits *only when a flag value changes*
 *   (`Object.keys(changes).length > 0 && setState(...)` in the provider). An `identify()` that
 *   swaps stores without changing any evaluated value therefore notifies nothing, so a
 *   render-time context read can stay stale indefinitely. `use-kiosk-ld-dispensary-reidentify`
 *   documents the same gotcha.
 * - The context key alone does not mean the *flags* belong to that store. `_app` seeds the initial
 *   client context with `ecomm-dispensary` from `window.reactEnv`, while the SSR bootstrap flags
 *   are evaluated with a fixed internal context and cached across requests, so the key can match
 *   while the values are still the bootstrap ones.
 *
 * So the identify call sites report completion here, and consumers subscribe. The recorded value
 * is LD's context *after* the identify settled, not the context that was requested: LD identify
 * responses are unordered, so an earlier store's identify can land last, and the flags then on
 * hand are that store's.
 */
type IdentifiedSnapshot = {
  dispensaryId: string | undefined;
  /**
   * Bumped on every published completion, including one that re-identifies the store already
   * recorded. Without it, restoring a store that is still the recorded one would be invisible to
   * `useSyncExternalStore`, which bails out when the snapshot is unchanged, and consumers would
   * not re-read the live context. That sequence is reachable: an unpublished `identifyLDUser` can
   * swap the context to another store, closing the gate, and the published identify that restores
   * this store carries the same id and, if every evaluated value is equal, no LD change event
   * either, so this is the only notification available.
   */
  revision: number;
};

let snapshot: IdentifiedSnapshot = { dispensaryId: undefined, revision: 0 };

const listeners = new Set<() => void>();

// Identity-stable between publishes, as useSyncExternalStore requires.
const getSnapshot = (): IdentifiedSnapshot => snapshot;

// The server never identifies, so nothing is store-authoritative during SSR. A module constant,
// again because the snapshot must be identity-stable.
const SERVER_SNAPSHOT: IdentifiedSnapshot = { dispensaryId: undefined, revision: 0 };
const getServerSnapshot = (): IdentifiedSnapshot => SERVER_SNAPSHOT;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The `ecomm-dispensary` key of the context LD currently holds, if any. */
export function readLdContextDispensaryId(client: LDClient | undefined): string | undefined {
  // Guarded because `getContext` is absent on older clients and on partial test doubles; treating
  // that as "no store identified" fails closed rather than throwing inside an identify callback.
  if (typeof client?.getContext !== 'function') {
    return undefined;
  }
  // LDClient types getContext() as non-null, but it returns null before the first identify.
  const context = client.getContext() as LDContext | null | undefined;
  return (context as Record<string, { key?: string } | undefined> | null | undefined)?.['ecomm-dispensary']?.key;
}

/**
 * Called by identify call sites once an `identify()` has settled. Always notifies, even when the
 * store is unchanged, because a completion is itself news: the live context may have been swapped
 * and restored in between without any flag value changing.
 */
export function markLdIdentifiedDispensary(dispensaryId: string | undefined): void {
  snapshot = { dispensaryId, revision: snapshot.revision + 1 };
  listeners.forEach((listener) => listener());
}

export function getLdIdentifiedDispensaryIdForTests(): string | undefined {
  return snapshot.dispensaryId;
}

export function resetLdIdentifiedDispensaryForTests(): void {
  snapshot = { dispensaryId: undefined, revision: 0 };
  listeners.clear();
}

export function useLdIdentifiedDispensaryId(): string | undefined {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot).dispensaryId;
}

/**
 * True once LaunchDarkly has completed an identify leaving this dispensary's context in place, so
 * the flags it is serving are this store's.
 *
 * Two conditions, because neither alone is sufficient:
 *
 * - The published signal proves the values on hand are post-identify for this store, which a
 *   context key cannot (the seeded context matches before the first identify, while the cached
 *   bootstrap values were evaluated with an internal context). It is also what makes this
 *   reactive, since LD emits no change event for an identify that leaves every value equal.
 * - The live context key proves nothing has swapped the context *since* that identify. Not every
 *   caller publishes: `identifyLDUser` in `shared/observability/ld-identify` is fire-and-forget
 *   and re-applies a context it snapshotted earlier, so a late checkout identify can restore the
 *   previous store's context and flags after navigation already published this store. Requiring
 *   the key as well closes the gate in that window instead of admitting the other store's values,
 *   and covers any future uninstrumented caller for free.
 */
export function useIsLdIdentifiedWithFlagsForDispensary(dispensaryId: string | null | undefined): boolean {
  const client = useLDClient();
  // Re-render when LD replaces the flag set, so the context below is re-read.
  useFlags();
  const identified = useLdIdentifiedDispensaryId();

  return Boolean(dispensaryId) && identified === dispensaryId && readLdContextDispensaryId(client) === dispensaryId;
}
